import { ContentsListType, RevisedItem } from '#/app/(nlp)/(try)/data/types';
import type { REVISIONCONFIGTYPE } from '#/hooks/use-revision-config';
import { fetchCorrectError } from '#/lib/fetch-correct-error';

// import { ReviseHelp } from '#/protobuf/bareun/revision_service_pb';
import { SelectItemType } from 'types/common';

const sortByScoreDesc = <T extends { score?: number }>(arr: T[]) =>
  [...arr].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

export async function getSentencesListStream(
  contents: string[],
  customDict: SelectItemType[],
  configType: REVISIONCONFIGTYPE,
  apiKey: string,
  onProgress?: (current: number) => void
) {
  const helpsMap = new Map<string, any>();
  let totalTokenCount = 0;
  const results: {
    info: string;
    content: ContentsListType;
  }[] = [];

  for (const content of contents) {
    let contentItem: ContentsListType;

    const correctError = await fetchCorrectError(
      content,
      apiKey,
      customDict,
      configType
    );

    if (correctError) {
      totalTokenCount += correctError.tokenCount;
      const sentencesList: RevisedItem[] = [];
      const insertCorrect: {
        begin_offset: number;
        endLength: number;
        originLength: number;
        arrayIndex: number;
      }[] = [];

      correctError.helps.forEach(([key, value]) => {
        if (!helpsMap.has(key)) {
          helpsMap.set(key, value);
        }
      });

      correctError.revisedBlocks.forEach((block, index) => {
        let isPush = true;

        insertCorrect.forEach((correct) => {
          if (
            correct.begin_offset <= block.origin.beginOffset &&
            correct.endLength >= block.origin.beginOffset
          ) {
            isPush = false;

            // 기존 개체의 revision 리스트 복제(없으면 빈 배열)
            const existing = sentencesList[correct.arrayIndex];
            const baseText =
              existing?.revisions?.[0]?.revised ??
              existing?.revised ??
              existing?.origin ??
              '';

            const merged: typeof existing.revisions = [
              ...(existing?.revisions ?? [])
            ];

            // 새 후보들을 기존 문맥(baseText)에 치환 적용해서 병합
            block.revisions?.forEach((ch) => {
              const revisedText = baseText.replace(
                block.origin.content,
                ch.revised
              );
              merged.push({
                ...ch,
                revised: revisedText
              });
            });

            // 점수 기준 내림차순 정렬 후, 최상 후보를 초기 표시로 설정
            const sorted = sortByScoreDesc(merged);
            if (existing) {
              existing.revisions = sorted;
              existing.revisionIndex = 0;
              existing.revised = sorted[0]?.revised ?? existing.revised;
              existing.category =
                (sorted[0]?.category as number | undefined) ??
                existing.category;
              existing.nested.push(block);
            }
          }
        });

        if (isPush) {
          // 새 블록일 경우: block.revisions에서 최고 점수 후보를 초기 표시로
          // (주의) 이 분기는 "치환 전후"의 스킴을 프로젝트에 맞게 유지
          // 여기서는 block.revised가 "치환된 결과"가 아니라 후보 단위 텍스트라 가정하지 않고,
          // 항상 candidates에서 최상점을 사용
          const sortedCandidates = sortByScoreDesc(block.revisions ?? []);
          const best = sortedCandidates[0] ?? {
            revised: block.revised,
            category: block.revisions?.[0]?.category ?? 'UNKNOWN',
            score: 0
          };

          insertCorrect.push({
            begin_offset: block.origin.beginOffset,
            endLength: block.origin.beginOffset + block.origin.content.length,
            originLength: block.origin.length,
            arrayIndex: sentencesList.length
          });

          sentencesList.push({
            origin: block.origin.content,
            revised: best.revised, // ✅ 최상 점수 후보로 초기 표시
            category: best.category || '', // ✅ 최상 점수 후보의 카테고리
            revisionIndex: 0, // ✅ 0으로 고정(정렬 후 최상 후보가 0번)
            updated: '',
            beginOffset: block.origin.beginOffset,
            wordLength: block.origin.length,
            nested: block.nested,
            revisions: sortedCandidates, // ✅ 점수 내림차순으로 정렬된 후보 리스트
            stateType: 'normal',
            lemma: block.lemma,
            pos: block.pos
          });
        }
      });
      if (correctError?.whitespaceCleanupRangesList) {
        for (const space of correctError.whitespaceCleanupRangesList) {
          const originSubstring = correctError?.origin.substring(
            space.offset,
            space.offset + space.length
          );
          sentencesList.push({
            origin: originSubstring,
            revised: '',
            category: 'SPACE',
            revisionIndex: 0,
            beginOffset: space.offset,
            wordLength: space.length,
            updated: '',
            stateType: 'normal',
            revisions: [],
            nested: [],
            lemma: '',
            pos: 'SPACE'
          });
        }
      }

      sentencesList.sort((a, b) => a.beginOffset - b.beginOffset);

      contentItem = {
        origin: correctError.origin,
        sentencesList
      };
    } else {
      contentItem = {
        origin: content,
        sentencesList: []
      };
    }

    results.push({
      info: '확인되지않는 챕터',
      content: contentItem
    });

    // 진행 상황 업데이트
    if (onProgress) {
      onProgress(results.length);
    }
  }

  return {
    results,
    helpsMap: Array.from(helpsMap.entries()),
    totalTokenCount
  };
}
